import { type ReactNode, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@nosible/shared/components/ui/alert-dialog";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { Label } from "@nosible/shared/components/ui/label";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import { trpc } from "@/admin/lib/trpc";
import { StdPlanCard } from "@nosible/shared/components/PlanCards";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Crown,
  Rocket,
  Shield,
  User,
  Zap,
} from "lucide-react";

const planIcons: Record<string, ReactNode> = {
  free: <Zap className="h-6 w-6" />,
  pro: <Rocket className="h-6 w-6" />,
  enterprise: <Crown className="h-6 w-6" />,
};

const planColors: Record<string, string> = {
  free: "border-chromium-mist/30",
  pro: "border-neon-green/50 ring-2 ring-neon-green/20",
  enterprise: "border-violet-pulse/50 ring-2 ring-violet-pulse/20",
};

export function AdminSubscriptionUpgrade() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const [note, setNote] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!subscriptionId }
  );

  const {
    data: plans,
    isLoading: plansLoading,
  } = trpc.subscription.getActivePlans.useQuery(undefined, {
    enabled: true,
  });

  const upgrade = trpc.subscription.mutation.adminUpgradeSubscription.useMutation({
    onSuccess: () => {
      setConfirmOpen(false);
      setSuccess(true);
    },
  });

  const selectedPlan = useMemo(() => {
    if (!plans || !selectedPlanId) return null;
    return plans.find((p: (typeof plans)[number]) => p.id === selectedPlanId) ?? null;
  }, [plans, selectedPlanId]);

  const isLoading = subscriptionLoading || plansLoading;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (subscriptionError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{subscriptionError.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!plans || !subscriptionId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Unable to load subscription or plans</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const currentPlan = subscription?.plan?.name;
  const ownerName = subscription?.owner?.name;
  const isOrg = subscription?.owner?.type === "organization";
  const isPending = upgrade.isPending;

  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!selectedPlanId) return;

    upgrade.mutate({
      subscriptionId,
      planId: selectedPlanId,
      note,
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-pulse/20 rounded-lg">
            <Shield className="h-6 w-6 text-violet-pulse" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-silica font-display">Admin Upgrade</h1>
            <p className="text-chromium-mist">
              Change a subscription plan without Stripe checkout
            </p>
          </div>
        </div>
      </div>

      {success ? (
        <Card className="border-neon-green">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <CheckCircle2 className="h-16 w-16 text-neon-green mx-auto mb-4" />
              <h2 className="text-xl font-bold text-silica mb-2">Upgrade Complete</h2>
              <p className="text-chromium-mist">The subscription has been updated.</p>
              <div className="mt-6">
                <Button onClick={() => navigate(`/subscriptions/${subscriptionId}`)}>
                  Back to Subscription
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>Who this subscription belongs to and current plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                {isOrg ? (
                  <Building2 className="h-4 w-4 text-chromium-mist" />
                ) : (
                  <User className="h-4 w-4 text-chromium-mist" />
                )}
                <span className="text-chromium-mist">
                  {isOrg ? "Organization" : "Personal"}: {ownerName ?? "Unknown"}
                </span>
                <span className="text-chromium-mist">•</span>
                <span className="text-chromium-mist">Current Plan:</span>
                <Badge className="capitalize">{currentPlan ?? "—"}</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Admin Note</CardTitle>
              <CardDescription>Optional internal note for audit trail</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Reason for upgrade (optional)"
                />
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan: (typeof plans)[number]) => {
              const isCurrent = !!currentPlan && plan.name === currentPlan;
              const icon = planIcons[plan.name.toLowerCase()] || <Zap className="h-6 w-6" />;
              const colorClass = planColors[plan.name.toLowerCase()] || "border-slate-core";
              const iconClassName = plan.name.toLowerCase() === "pro"
                ? "bg-neon-green/20 text-neon-green"
                : plan.name.toLowerCase() === "enterprise"
                  ? "bg-violet-pulse/20 text-violet-pulse"
                  : "bg-slate-core text-chromium-mist";
              const actionClassName = plan.name.toLowerCase() === "pro"
                ? "bg-neon-green text-obsidian hover:bg-neon-green/90"
                : plan.name.toLowerCase() === "enterprise"
                  ? "bg-violet-pulse hover:bg-violet-pulse/90"
                  : "";

              return (
                <StdPlanCard
                  key={plan.id}
                  plan={{ ...plan, keyFeatures: plan.keyFeatures ?? [] }}
                  icon={icon}
                  iconClassName={iconClassName}
                  colorClassName={`${colorClass}${isCurrent ? " bg-slate-core/30" : ""}`}
                  headerBadge={isCurrent ? (
                    <Badge variant="outline" className="mt-1 text-xs">Current Plan</Badge>
                  ) : undefined}
                  actionLabel={isCurrent ? "Current Plan" : `Set plan to ${plan.name}`}
                  actionClassName={actionClassName}
                  onAction={() => handleSelectPlan(plan.id)}
                  actionDisabled={isPending || isCurrent}
                  showMonthlySuffix={plan.monthlyPriceCents > 0 && plan.monthlyPriceCents !== -1}
                />
              );
            })}
          </div>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Admin Upgrade</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div>
                    <p>
                      You are about to change this subscription to{" "}
                      <strong className="capitalize">{selectedPlan?.name ?? ""}</strong>.
                    </p>
                    <p className="mt-4">
                      This action bypasses Stripe checkout.
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirm}
                  disabled={isPending || !selectedPlanId}
                  className="bg-violet-pulse hover:bg-violet-pulse/90"
                >
                  {isPending ? "Updating..." : "Confirm Upgrade"}
                </AlertDialogAction>
              </AlertDialogFooter>
              {upgrade.error && (
                <div className="mt-4 text-sm text-red-400">Error: {upgrade.error.message}</div>
              )}
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
